import { LoadingOutlined } from '@ant-design/icons';
import { T, useTranslate } from '@tolgee/react';
import { Dropdown, Input, Menu } from 'antd';
import dayjs from 'dayjs';
import Link from 'next/link';
import { useCallback, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { CopySvg, DeleteSvg } from '../../assets/icons';
import AnswerSvg from '../../assets/icons/AnswerSvg';
import ChartSvg from '../../assets/icons/ChartSvg';
import DownloadSvg from '../../assets/icons/DownloadSvg';
import FolderSvg from '../../assets/icons/FolderSvg';
import MoreSvg from '../../assets/icons/MoreSvg';
import PencilSvg from '../../assets/icons/PencilSvg';
import PlaySvg from '../../assets/icons/PlaySvg';
import ShareSvg from '../../assets/icons/ShareSvg';
import theme from '../../assets/theme';
import useAccessBadge, { LicenseCoarseLevels } from '../../hooks/useAccessBadge';
import useAttioEventTracking from '../../hooks/useAttioEventTracking';
import { useDeleteTemplate } from '../../hooks/useDeleteTemplate';
import useDuplicateTemplate from '../../hooks/useDuplicateTemplate';
import useEndAndStartNewLesson from '../../hooks/useEndAndStartNewLesson';
import { useMetrics } from '../../hooks/useMetrics';
import usePaywallPrompt from '../../hooks/usePaywallPrompt';
import usePresent from '../../hooks/usePresent';
import useRenameLesson from '../../hooks/useRenameLesson';
import useRouterMatch from '../../hooks/useRouterMatch';
import { usePaywall } from '../../providers/PaywallProvider/PaywallProvider';
import { usePremiumTestPrepPlaywall } from '../../providers/PremiumTestPrepPlaywallProvider/PremiumTestPrepPlaywallProvider';
import { PresentationDTO } from '../../services/backendService/types';
import { Template } from '../../services/TemplateService/Template.type';
import { isTouchDevice } from '../../utils/client';
import { exportPdf } from '../../utils/exportPdf';
import {
  isPremiumTestPrepPresentation,
  stampPremiumContentOnSlides,
} from '../../utils/premiumContentDetection';
import { SlideType as ActivitySlideType } from '../SlideComponents/slide.types';

// Type-safe unions for slide type checks
const AI_ACTIVITY_SLIDE_TYPES: readonly ActivitySlideType[] = [
  'aiAssessedAnswers',
  'aiAssessedImageAnswers',
];

const EXPORTABLE_SLIDE_TYPES: readonly ActivitySlideType[] = [
  'wordcloud',
  'slide',
  'answers',
  'aiAssessedAnswers',
  'aiAssessedImageAnswers',
  'poll',
  'canvas',
];

function isUnionMember<T extends string>(value: string, union: readonly T[]): value is T {
  return union.includes(value as T);
}
import {
  convertPresentationDTOToTemplateDataType,
  getTitle,
} from '../../utils/templateUtils';
import { Button } from '../_atoms/Button';
import ResumeLessonModal from '../_atoms/ResumeLessonModal';
import BaseModal from '../_molecules/BaseModal';
import { PresentationCard } from '../_molecules/PresentationCard';
import ShareLinkComponent from '../_molecules/ShareLinkComponent';
import DuplicateLessonToFolder, {
  DuplicateLessonData,
} from './DuplicateLessonToFolder/DuplicateLessonToFolder';
import { MoveToFolderData } from './MoveToFolder';
import MoveToFolder from './MoveToFolder/MoveToFolder';
import { MoveToTrashModal } from './MoveToTrashModal';

export const LessonsSection = ({
  lessons,
  maxOneRow,
  presentationsCacheKey,
  isWorkspace,
}: {
  lessons: PresentationDTO[] | undefined;
  maxOneRow?: boolean;
  presentationsCacheKey?: (string | boolean | undefined)[];
  isWorkspace?: boolean;
}) => {
  const { urlTenantId: tenantId } = useRouterMatch();
  const { t } = useTranslate();
  const metrics = useMetrics();
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const trackAttioEvent = useAttioEventTracking();
  const [hoveredLessonId, setHoveredLessonId] = useState<string>();
  const [isDownloadingPDF, setIsDownloadingPDF] = useState<boolean>();
  const [showShareModal, setShowShareModal] = useState<PresentationDTO>();
  const [isDeletingData, setIsDeletingData] = useState<Template>();
  const [movingLessonData, setMovingLessonData] = useState<MoveToFolderData>();
  const [duplicatingLessonData, setDuplicatingLessonData] = useState<
    DuplicateLessonData | undefined
  >();
  const [renamingLesson, setRenamingLesson] = useState<
    { id: string; title: string } | undefined
  >();

  // Present lesson state and hooks
  const [isOnGoingGameModalVisible, setIsOnGoingGameModalVisible] = useState<
    string | undefined
  >();
  const { presentLesson } = usePresent();
  const { renameLesson, isRenaming } = useRenameLesson();
  const { handleEndAndStartNewLesson } = useEndAndStartNewLesson();

  // Paywall functionality for PDF downloads for freemium users
  const { promptPaywall: promptPdfPaywall, paywallComponent } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });

  // Lesson limit paywall (teaching sessions)
  const {
    promptPaywall: promptLessonLimitPaywall,
    shouldPromptPaywall,
    isLessonLimitLoading,
  } = usePaywall();

  // Premium test prep playwall
  const {
    checkAndPromptPremiumTestPrepPaywall,
    shouldShowPremiumIndicator,
    premiumTestPrepHasReachedLimit,
  } = usePremiumTestPrepPlaywall();

  // Check if user is on freemium plan
  const isFreemiumUser =
    userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.freemium;

  const handleExportAsPdf = (template: PresentationDTO) => {
    const slides = template?.content.slides ?? [];
    const slidesToBeExported = slides.filter(slide =>
      isUnionMember(slide.type, EXPORTABLE_SLIDE_TYPES),
    );

    exportPdf({
      slides: slidesToBeExported,
      lessonAttachments: template.content.attachments,
      fileName: `${getTitle({
        title: template.content.title,
        slides: template.content.slides,
        maxLength: 30,
      })}`,
      onSuccess: () => setIsDownloadingPDF(false),
    });
  };

  const handleDownloadPDFClicked = useCallback(
    (template: PresentationDTO) => {
      // Check if freemium user trying to download PDF
      if (isFreemiumUser) {
        promptPdfPaywall({
          label: 'Download lesson as PDF from lessons page',
          description: 'Lesson PDF download functionality',
        });
        return;
      }

      setIsDownloadingPDF(true);
      metrics.logEvent('DownloadPDF', { from: 'Lessons.Download.PDF' });
      handleExportAsPdf(template);
    },
    [metrics, isFreemiumUser, promptPdfPaywall],
  );

  const {
    deleteTemplate: deleteLesson,
    isDeletingTemplate: isDeletingLesson,
  } = useDeleteTemplate({
    currentCacheKey: presentationsCacheKey,
  });

  // Present lesson logic similar to lesson preview page
  const handlePresent = useCallback(
    (lesson: PresentationDTO) => {
      if (isLessonLimitLoading) return;
      if (shouldPromptPaywall()) {
        promptLessonLimitPaywall('lessons-page-start-lesson');
        return;
      }

      if (lesson.content.slides && lesson.content.slides?.length > 0) {
        const slides = lesson.content.slides;
        const isPremiumTestPrep = isPremiumTestPrepPresentation(slides);

        const doPresent = () => {
          metrics.logEvent('Lessons.Present', {
            templateId: lesson.id,
            withModeration: false,
            fromBeginning: lesson.content.activeLivePresentation?.isOngoing
              ? false
              : true,
            isPremiumTestPrep,
          });

          if (lesson.content.activeLivePresentation?.isOngoing) {
            setIsOnGoingGameModalVisible(lesson.id);
            return;
          }

          const templateForPresent = {
            id: lesson.id,
            type: 'template' as const,
            parentId: lesson.parentId,
            tenantId: lesson.tenantId,
            isPrivate: lesson.isPrivate,
            createdAt: new Date(lesson.createdAt).getTime(),
            modifiedAt: new Date(lesson.modifiedAt).getTime(),
            creator: {
              uid: lesson.creatorId,
            },
            members: [],
            content: {
              slides: stampPremiumContentOnSlides(slides),
              title: lesson.content.title,
              activeGame: lesson.content.activeLivePresentation
                ? {
                    id: lesson.content.activeLivePresentation.id,
                    isOngoing: true,
                  }
                : undefined,
              rounds: null,
              gameIds: [],
            },
            attachments: lesson.content.attachments,
            metadata: undefined,
            creatorId: lesson.creatorId,
            title: lesson.content.title,
          };

          presentLesson({
            template: templateForPresent,
            method: 'present',
            fromBeginning: true,
            gameId: lesson.content.activeLivePresentation?.id,
          });
        };

        if (isPremiumTestPrep) {
          const wasIntercepted = checkAndPromptPremiumTestPrepPaywall(
            slides,
            'lessons-page-start-lesson',
            doPresent,
          );
          if (wasIntercepted) return;
        }

        doPresent();
      }
    },
    [
      metrics,
      presentLesson,
      shouldPromptPaywall,
      promptLessonLimitPaywall,
      isLessonLimitLoading,
      checkAndPromptPremiumTestPrepPaywall,
    ],
  );

  const handleStartNewLesson = useCallback(
    (lesson: PresentationDTO) => {
      if (lesson.content.activeLivePresentation?.isOngoing) {
        const templateForEndAndStart = {
          id: lesson.id,
          type: 'template' as const,
          parentId: lesson.parentId,
          tenantId: lesson.tenantId,
          isPrivate: lesson.isPrivate,
          createdAt: new Date(lesson.createdAt).getTime(),
          modifiedAt: new Date(lesson.modifiedAt).getTime(),
          creator: {
            uid: lesson.creatorId,
          },
          members: [],
          content: {
            slides: lesson.content.slides,
            title: lesson.content.title,
            activeGame: {
              id: lesson.content.activeLivePresentation.id,
              isOngoing: true,
            },
            rounds: null,
            gameIds: [],
          },
          metadata: lesson.aiContent || {},
          creatorId: lesson.creatorId,
          title: lesson.content.title,
          attachments: lesson.content.attachments,
        };

        handleEndAndStartNewLesson({
          gameId: lesson.content.activeLivePresentation.id,
          template: templateForEndAndStart,
        });
        setIsOnGoingGameModalVisible(undefined);
      }
    },
    [handleEndAndStartNewLesson],
  );

  const handleResumeLesson = useCallback(
    (lesson: PresentationDTO) => {
      // Convert PresentationDTO to Template format for presentLesson
      const templateForResume = {
        id: lesson.id,
        type: 'template' as const,
        parentId: lesson.parentId,
        tenantId: lesson.tenantId,
        isPrivate: lesson.isPrivate,
        createdAt: new Date(lesson.createdAt).getTime(),
        modifiedAt: new Date(lesson.modifiedAt).getTime(),
        creator: {
          uid: lesson.creatorId,
        },
        members: [],
        content: {
          slides: lesson.content.slides,
          title: lesson.content.title,
          activeGame: lesson.content.activeLivePresentation
            ? {
                id: lesson.content.activeLivePresentation.id,
                isOngoing: true,
              }
            : undefined,
          rounds: null,
          gameIds: [],
        },
        attachments: lesson.content.attachments,
        metadata: lesson.aiContent || {},
        creatorId: lesson.creatorId,
        title: lesson.content.title,
      };

      // Present the lesson from where it left off
      presentLesson({
        template: templateForResume,
        method: 'present',
        fromBeginning: false,
        gameId: lesson.content.activeLivePresentation?.id,
      });
      setIsOnGoingGameModalVisible(undefined);
    },
    [presentLesson],
  );

  const handleEndGame = useCallback(() => {
    // Not used in this context
  }, []);

  const handleRenameLesson = useCallback(() => {
    if (!renamingLesson || isRenaming) return;
    metrics.logEvent('Lessons.RenameLesson');
    renameLesson(
      { lessonId: renamingLesson.id, title: renamingLesson.title },
      { onSuccess: () => setRenamingLesson(undefined) },
    );
  }, [renamingLesson, isRenaming, metrics, renameLesson]);

  const renderMenu = (lesson: PresentationDTO) => (
    <Menu>
      <Menu.Item key={'play'} style={{ padding: 0 }}>
        <DropdownMenuItemButton onClick={() => handlePresent(lesson)}>
          <PlaySvg /> <T keyName="Start Lesson" defaultValue="Start Lesson" />
        </DropdownMenuItemButton>
      </Menu.Item>
      <Menu.Item
        key={'rename'}
        style={{ padding: 0 }}
        disabled={lesson.creatorId !== userAccessBadge?.user.id}
        onClick={() => {
          metrics.logEvent('Lessons.RenameLesson.Start');
          setRenamingLesson({
            id: lesson.id,
            title: lesson.content.title || '',
          });
        }}
      >
        <DropdownMenuItemButton disabled={lesson.creatorId !== userAccessBadge?.user.id}>
          <PencilSvg /> <T keyName="Rename" defaultValue="Rename" />
        </DropdownMenuItemButton>
      </Menu.Item>
      <Menu.Item
        key={'edit'}
        style={{ padding: 0 }}
        disabled={lesson.creatorId !== userAccessBadge?.user.id}
      >
        <Link href={`/${tenantId}/lessons/${lesson.id}/edit`}>
          <a>
            <DropdownMenuItemButton>
              <AnswerSvg /> <T keyName="Edit" defaultValue="Edit" />
            </DropdownMenuItemButton>
          </a>
        </Link>
      </Menu.Item>
      <div
        key="divider-play"
        style={{ width: '100%', height: 1, backgroundColor: '#C4C4C4' }}
      />
      <Menu.Item
        key={'share'}
        style={{ padding: 0 }}
        onClick={() => {
          setShowShareModal(lesson);
          // Paused until decision on product event
          // trackAttioEvent('lesson_shared', {
          //   organizationId: userAccessBadge?.organization?.id,
          //   userId: userAccessBadge?.user?.id,
          //   userRole: userAccessBadge?.user?.profileInfo?.role,
          //   lessonId: lesson.id,
          //   lessonTitle: lesson.content.title,
          //   email: userAccessBadge?.user?.email,
          //   firstName: userAccessBadge?.user?.firstName,
          //   lastName: userAccessBadge?.user?.lastName,
          // });
        }}
      >
        <DropdownMenuItemButton>
          <ShareSvg /> <T keyName="Share" defaultValue="Share" />
        </DropdownMenuItemButton>
      </Menu.Item>
      <Menu.Item key={'seereports'} style={{ padding: 0 }}>
        <Link href={`/${tenantId}/reports/?templateId=${lesson.id}`}>
          <a>
            <DropdownMenuItemButton>
              <ChartSvg /> <T keyName="See reports" defaultValue="See reports" />{' '}
              {lesson.content.livePresentationIds &&
              lesson.content.livePresentationIds.length > 0
                ? `(${lesson.content.livePresentationIds?.length})`
                : ''}
            </DropdownMenuItemButton>
          </a>
        </Link>
      </Menu.Item>
      <Menu.Item key={'downloadPdf'} style={{ padding: 0 }}>
        <DropdownMenuItemButton
          onClick={() => handleDownloadPDFClicked(lesson)}
          disabled={isDownloadingPDF}
        >
          {isDownloadingPDF ? <LoadingOutlined /> : <DownloadSvg />}{' '}
          <T keyName="Download PDF" defaultValue="Download PDF" />
        </DropdownMenuItemButton>
      </Menu.Item>
      <div
        key="divider-folder"
        style={{ width: '100%', height: 1, backgroundColor: '#C4C4C4' }}
      />
      <Menu.Item key={'duplicate'} style={{ padding: 0 }}>
        <DropdownMenuItemButton
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            metrics.logEvent('Lessons.Template.Duplicate');

            setDuplicatingLessonData({
              id: lesson.id,
              parentId: lesson.parentId,
              isPrivate: isWorkspace ? false : true,
              slides: lesson.content.slides,
              title: lesson.content.title,
            });
          }}
        >
          <CopySvg /> <T keyName="Duplicate" defaultValue="Duplicate" />
        </DropdownMenuItemButton>
      </Menu.Item>
      <Menu.Item
        key={'move'}
        style={{ padding: 0 }}
        disabled={lesson.creatorId !== userAccessBadge?.user.id}
        onClick={() =>
          setMovingLessonData({
            id: lesson.id,
            type: 'lesson',
            title: lesson.content.title,
            parentId: lesson.parentId,
            isPrivate: lesson.isPrivate,
            slides: lesson.content.slides,
          })
        }
      >
        <DropdownMenuItemButton disabled={lesson.creatorId !== userAccessBadge?.user.id}>
          <FolderSvg /> <T keyName="Move" defaultValue="Move" />
        </DropdownMenuItemButton>
      </Menu.Item>
      <div
        key="divider-trash"
        style={{ width: '100%', height: 1, backgroundColor: '#C4C4C4' }}
      />
      <Menu.Item
        key={'delete'}
        style={{ padding: 0, color: theme.colors.red }}
        disabled={lesson.creatorId !== userAccessBadge?.user.id}
        onClick={() =>
          setIsDeletingData(convertPresentationDTOToTemplateDataType(lesson))
        }
      >
        <DropdownMenuItemButton disabled={lesson.creatorId !== userAccessBadge?.user.id}>
          <DeleteSvg /> <T keyName="Move to trash" defaultValue="Move to trash" />
        </DropdownMenuItemButton>
      </Menu.Item>
    </Menu>
  );

  const isTouch = isTouchDevice();

  return (
    <>
      {paywallComponent}
      <SectionGridContainer>
        <SectionGrid $maxOneRow={maxOneRow}>
          {lessons?.map(lesson => (
            <div
              key={lesson.id}
              style={{ height: '100%', width: '100%', position: 'relative' }}
              onMouseEnter={() => setHoveredLessonId(lesson.id)}
              onMouseLeave={() => setHoveredLessonId(undefined)}
            >
              <Link href={`/${tenantId}/lessons/${lesson.id}/edit`}>
                <a>
                  <PresentationCard
                    isHovering={lesson.id === hoveredLessonId}
                    slides={lesson.content.slides || []}
                    subTitle={`Edited:
        ${dayjs(lesson.modifiedAt).format('MMMM DD, YYYY - hh:mma')}`}
                    isGenerator={!!lesson.aiContent?.prompts?.length}
                    title={lesson.content.title}
                    lessonAttachments={lesson.content.attachments || []}
                    isPremiumTestPrep={shouldShowPremiumIndicator(
                      lesson.content.slides || [],
                    )}
                    hasReachedPremiumTestPrepLimit={premiumTestPrepHasReachedLimit}
                  />
                  <div
                    onClick={e => {
                      e.stopPropagation();
                    }}
                  >
                    {(lesson.id === hoveredLessonId || isTouch) && (
                      <Dropdown
                        overlay={renderMenu(lesson)}
                        trigger={['click']}
                        placement={'bottomRight'}
                      >
                        <MoreButton notext icon={<MoreSvg />}></MoreButton>
                      </Dropdown>
                    )}
                  </div>
                </a>
              </Link>
            </div>
          ))}
        </SectionGrid>
        {/* ------------ SHARE LESSON ----------------- */}
        {showShareModal && (
          <BaseModal
            title={t('shareTemplate', 'Share "{title}"', {
              title: getTitle({
                title: showShareModal.content.title,
                slides: showShareModal.content.slides,
                maxLength: 75,
              }),
            })}
            open={!!showShareModal}
            onCancel={() => {
              setShowShareModal(undefined);
            }}
          >
            <ShareLinkComponent
              templateId={showShareModal.id}
              isGeneratedByAI={!!showShareModal.content?.metadata?.['aiTopic']}
            />
          </BaseModal>
        )}
        {/* ------------ MOVE LESSON ----------------- */}
        {!!movingLessonData && (
          <MoveToFolder
            onCancel={() => setMovingLessonData(undefined)}
            data={movingLessonData}
            onDone={() => setMovingLessonData(undefined)}
          />
        )}
        {/* ------------ DUPLICATE LESSON ----------------- */}
        {!!duplicatingLessonData && (
          <DuplicateLessonToFolder
            onCancel={() => setDuplicatingLessonData(undefined)}
            data={duplicatingLessonData}
            onDone={() => setDuplicatingLessonData(undefined)}
          />
        )}
        {/* ------------ RENAME LESSON ----------------- */}
        {renamingLesson && (
          <BaseModal
            title={t('Rename Lesson', {
              defaultValue: 'Rename "{{title}}"',
              title: renamingLesson.title || t('Untitled'),
            })}
            open
            onCancel={() => setRenamingLesson(undefined)}
          >
            <Input
              placeholder={t('New name')}
              autoFocus
              maxLength={100}
              value={renamingLesson.title}
              disabled={isRenaming}
              onChange={(
                event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
              ) =>
                setRenamingLesson({
                  ...renamingLesson,
                  title: event.target.value,
                })
              }
              onPressEnter={handleRenameLesson}
            />
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: '15px',
              }}
            >
              <Button
                type="tertiary"
                styles={{ border: 'none', color: theme.colors.black, boxShadow: 'none' }}
                onClick={() => setRenamingLesson(undefined)}
              >
                {t('Cancel')}
              </Button>
              <Button disabled={isRenaming} onClick={handleRenameLesson}>
                {isRenaming ? (
                  <>
                    <LoadingOutlined /> {t('Renaming')}
                  </>
                ) : (
                  t('Rename')
                )}
              </Button>
            </div>
          </BaseModal>
        )}
        {/* ------------ MOVE LESSON TO TRASH ----------------- */}
        {isDeletingData && (
          <MoveToTrashModal
            handleDelete={() => {
              deleteLesson({ id: isDeletingData.id });
              setIsDeletingData(undefined);
            }}
            deleteData={isDeletingData}
            isDeletingData={isDeletingLesson}
            folders={[]}
            setDeleteData={t => setIsDeletingData(t as Template | undefined)}
          />
        )}
        {/* ------------ RESUME LESSON MODAL ----------------- */}
        {isOnGoingGameModalVisible &&
          (() => {
            const currentLesson = lessons?.find(l => l.id === isOnGoingGameModalVisible);
            if (!currentLesson) return null;
            return (
              <ResumeLessonModal
                isOnGoingGameModalVisible={!!isOnGoingGameModalVisible}
                onStartNewLesson={() => handleStartNewLesson(currentLesson)}
                onResumeLesson={() => handleResumeLesson(currentLesson)}
                currentPage="lessonPage"
                onEndLesson={handleEndGame}
                onCancel={() => setIsOnGoingGameModalVisible(undefined)}
              />
            );
          })()}
      </SectionGridContainer>
    </>
  );
};
export const WorkspaceLessonsSection = ({
  lessons,
  maxOneRow,
  presentationsCacheKey,
  isWorkspace,
}: {
  lessons: PresentationDTO[] | undefined;
  maxOneRow?: boolean;
  presentationsCacheKey?: (string | boolean | undefined)[];
  isWorkspace?: boolean;
}) => {
  const { urlTenantId: tenantId, router } = useRouterMatch();
  const { t } = useTranslate();
  const metrics = useMetrics();
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const trackAttioEvent = useAttioEventTracking();
  const [hoveredLessonId, setHoveredLessonId] = useState<string>();
  const [isDownloadingPDF, setIsDownloadingPDF] = useState<boolean>();
  const [showShareModal, setShowShareModal] = useState<PresentationDTO>();
  const [isDeletingData, setIsDeletingData] = useState<Template>();
  const [movingLessonData, setMovingLessonData] = useState<MoveToFolderData>();
  const [duplicatingLessonData, setDuplicatingLessonData] = useState<
    DuplicateLessonData | undefined
  >();
  const [isCopyAndEdit, setIsCopyAndEdit] = useState<boolean>(false);
  const [renamingLesson, setRenamingLesson] = useState<
    { id: string; title: string } | undefined
  >();

  // Present lesson state and hooks
  const [isOnGoingGameModalVisible, setIsOnGoingGameModalVisible] = useState<
    string | undefined
  >();
  const [creatingLessonId, setCreatingLessonId] = useState<string | undefined>();

  const { presentLesson, isCreatingGame } = usePresent();
  const { renameLesson, isRenaming } = useRenameLesson();

  const { duplicateTemplate, isDuplicatingTemplate } = useDuplicateTemplate();
  const [openDropdownId, setOpenDropdownId] = useState<string>();
  const { handleEndAndStartNewLesson } = useEndAndStartNewLesson();

  // Lesson limit paywall (teaching sessions)
  const {
    promptPaywall: promptLessonLimitPaywall,
    shouldPromptPaywall,
    isLessonLimitLoading,
  } = usePaywall();

  // Premium test prep playwall
  const {
    checkAndPromptPremiumTestPrepPaywall: checkAndPromptPremiumTestPrepPaywallWorkspace,
    shouldShowPremiumIndicator: shouldShowPremiumIndicatorWorkspace,
    premiumTestPrepHasReachedLimit: premiumTestPrepHasReachedLimitWorkspace,
  } = usePremiumTestPrepPlaywall();

  // Reset creatingLessonId when game creation is complete
  useEffect(() => {
    if (!isCreatingGame) {
      setCreatingLessonId(undefined);
    }
  }, [isCreatingGame]);

  const handleCopyAndEdit = useCallback(
    (lesson: PresentationDTO) => {
      metrics.logEvent('Workspace.Lesson.AddToMyLesson', {
        isPrivate: lesson.isPrivate,
        isOwner: false,
      });

      if (!isDuplicatingTemplate && tenantId) {
        setIsCopyAndEdit(true);
        duplicateTemplate({
          templateId: lesson.id,
          isPrivate: true,
          onSuccessFunction: (template: Template) => {
            setIsCopyAndEdit(false);
            router.push(`/${tenantId}/lessons/${template.id}/edit`);
          },
        });
      }
    },
    [metrics, isDuplicatingTemplate, tenantId, duplicateTemplate, router],
  );

  const handleExportAsPdf = (template: PresentationDTO) => {
    const slides = template?.content.slides ?? [];
    const slidesToBeExported = slides.filter(slide =>
      isUnionMember(slide.type, EXPORTABLE_SLIDE_TYPES),
    );

    exportPdf({
      slides: slidesToBeExported,
      lessonAttachments: template.content.attachments,
      fileName: `${getTitle({
        title: template.content.title,
        slides: template.content.slides,
        maxLength: 30,
      })}`,
      onSuccess: () => setIsDownloadingPDF(false),
    });
  };

  const handleDownloadPDFClicked = useCallback(
    (template: PresentationDTO) => {
      setIsDownloadingPDF(true);
      metrics.logEvent('DownloadPDF', { from: 'Lessons.Download.PDF' });
      handleExportAsPdf(template);
    },
    [metrics],
  );

  const {
    deleteTemplate: deleteLesson,
    isDeletingTemplate: isDeletingLesson,
  } = useDeleteTemplate({
    currentCacheKey: presentationsCacheKey,
  });

  // Present lesson logic similar to lesson preview page
  const handlePresent = useCallback(
    (lesson: PresentationDTO) => {
      if (isLessonLimitLoading) return;
      if (shouldPromptPaywall()) {
        promptLessonLimitPaywall('workspace-lessons-page-start-lesson');
        return;
      }

      if (lesson.content.slides && lesson.content.slides?.length > 0) {
        const slides = lesson.content.slides;
        const isPremiumTestPrep = isPremiumTestPrepPresentation(slides);

        const doPresent = () => {
          metrics.logEvent('Lessons.Present', {
            templateId: lesson.id,
            withModeration: false,
            fromBeginning: lesson.content.activeLivePresentation?.isOngoing
              ? false
              : true,
            isPremiumTestPrep,
          });

          if (lesson.content.activeLivePresentation?.isOngoing) {
            setIsOnGoingGameModalVisible(lesson.id);
            return;
          }

          setCreatingLessonId(lesson.id);

          const templateForPresent = {
            id: lesson.id,
            type: 'template' as const,
            parentId: lesson.parentId,
            tenantId: lesson.tenantId,
            isPrivate: lesson.isPrivate,
            createdAt: new Date(lesson.createdAt).getTime(),
            modifiedAt: new Date(lesson.modifiedAt).getTime(),
            creator: {
              uid: lesson.creatorId,
            },
            members: [],
            content: {
              slides: stampPremiumContentOnSlides(slides),
              title: lesson.content.title,
              activeGame: lesson.content.activeLivePresentation
                ? {
                    id: lesson.content.activeLivePresentation.id,
                    isOngoing: true,
                  }
                : undefined,
              rounds: null,
              gameIds: [],
            },
            attachments: lesson.content.attachments,
            metadata: undefined,
            creatorId: lesson.creatorId,
            title: lesson.content.title,
          };

          presentLesson({
            template: templateForPresent,
            method: 'present',
            fromBeginning: true,
            gameId: lesson.content.activeLivePresentation?.id,
          });
        };

        if (isPremiumTestPrep) {
          const wasIntercepted = checkAndPromptPremiumTestPrepPaywallWorkspace(
            slides,
            'workspace-lessons-page-start-lesson',
            doPresent,
          );
          if (wasIntercepted) return;
        }

        doPresent();
      }
    },
    [
      metrics,
      presentLesson,
      shouldPromptPaywall,
      promptLessonLimitPaywall,
      isLessonLimitLoading,
      checkAndPromptPremiumTestPrepPaywallWorkspace,
    ],
  );

  const handleStartNewLesson = useCallback(
    (lesson: PresentationDTO) => {
      if (lesson.content.activeLivePresentation?.isOngoing) {
        const templateForEndAndStart = {
          id: lesson.id,
          type: 'template' as const,
          parentId: lesson.parentId,
          tenantId: lesson.tenantId,
          isPrivate: lesson.isPrivate,
          createdAt: new Date(lesson.createdAt).getTime(),
          modifiedAt: new Date(lesson.modifiedAt).getTime(),
          creator: {
            uid: lesson.creatorId,
          },
          members: [],
          content: {
            slides: lesson.content.slides,
            title: lesson.content.title,
            activeGame: {
              id: lesson.content.activeLivePresentation.id,
              isOngoing: true,
            },
            rounds: null,
            gameIds: [],
          },
          metadata: lesson.aiContent || {},
          creatorId: lesson.creatorId,
          title: lesson.content.title,
          attachments: lesson.content.attachments,
        };

        handleEndAndStartNewLesson({
          gameId: lesson.content.activeLivePresentation.id,
          template: templateForEndAndStart,
        });
        setIsOnGoingGameModalVisible(undefined);
      }
    },
    [handleEndAndStartNewLesson],
  );

  const handleResumeLesson = useCallback(
    (lesson: PresentationDTO) => {
      // Convert PresentationDTO to Template format for presentLesson
      const templateForResume = {
        id: lesson.id,
        type: 'template' as const,
        parentId: lesson.parentId,
        tenantId: lesson.tenantId,
        isPrivate: lesson.isPrivate,
        createdAt: new Date(lesson.createdAt).getTime(),
        modifiedAt: new Date(lesson.modifiedAt).getTime(),
        creator: {
          uid: lesson.creatorId,
        },
        members: [],
        content: {
          slides: lesson.content.slides,
          title: lesson.content.title,
          activeGame: lesson.content.activeLivePresentation
            ? {
                id: lesson.content.activeLivePresentation.id,
                isOngoing: true,
              }
            : undefined,
          rounds: null,
          gameIds: [],
        },
        attachments: lesson.content.attachments,
        metadata: lesson.aiContent || {},
        creatorId: lesson.creatorId,
        title: lesson.content.title,
      };

      // Present the lesson from where it left off
      presentLesson({
        template: templateForResume,
        method: 'present',
        fromBeginning: false,
        gameId: lesson.content.activeLivePresentation?.id,
      });
      setIsOnGoingGameModalVisible(undefined);
    },
    [presentLesson],
  );

  const handleEndGame = useCallback(() => {
    // Not used in this context
  }, []);

  const handleRenameLesson = useCallback(() => {
    if (!renamingLesson || isRenaming) return;
    metrics.logEvent('Lessons.RenameLesson');
    renameLesson(
      { lessonId: renamingLesson.id, title: renamingLesson.title },
      { onSuccess: () => setRenamingLesson(undefined) },
    );
  }, [renamingLesson, isRenaming, metrics, renameLesson]);

  const renderMenu = (lesson: PresentationDTO) => (
    <Menu>
      <Menu.Item key={'play'} style={{ padding: 0 }}>
        <DropdownMenuItemButton onClick={() => handlePresent(lesson)}>
          <PlaySvg /> <T keyName="Start Lesson" defaultValue="Start Lesson" />
        </DropdownMenuItemButton>
      </Menu.Item>
      {lesson.creatorId === userAccessBadge?.user.id && (
        <Menu.Item
          key={'rename'}
          style={{ padding: 0 }}
          onClick={() => {
            metrics.logEvent('Lessons.RenameLesson.Start');
            setRenamingLesson({
              id: lesson.id,
              title: lesson.content.title || '',
            });
          }}
        >
          <DropdownMenuItemButton>
            <PencilSvg /> <T keyName="Rename" defaultValue="Rename" />
          </DropdownMenuItemButton>
        </Menu.Item>
      )}
      {lesson.creatorId === userAccessBadge?.user.id && (
        <Menu.Item key={'edit'} style={{ padding: 0 }}>
          <Link href={`/${tenantId}/lessons/${lesson.id}/edit`}>
            <a>
              <DropdownMenuItemButton>
                <AnswerSvg /> <T keyName="Edit" defaultValue="Edit" />
              </DropdownMenuItemButton>
            </a>
          </Link>
        </Menu.Item>
      )}
      {lesson.creatorId !== userAccessBadge?.user.id && (
        <Menu.Item key={'copy-and-edit'} style={{ padding: 0 }}>
          <DropdownMenuItemButton
            onClick={() => handleCopyAndEdit(lesson)}
            disabled={isCopyAndEdit || isDuplicatingTemplate}
          >
            {isDuplicatingTemplate ? <LoadingOutlined /> : <AnswerSvg />}{' '}
            <T keyName="Copy & Edit" defaultValue="Copy & Edit" />
          </DropdownMenuItemButton>
        </Menu.Item>
      )}
      <div
        key="divider-play"
        style={{ width: '100%', height: 1, backgroundColor: '#C4C4C4' }}
      />

      <Menu.Item
        key={'share'}
        style={{ padding: 0 }}
        onClick={() => {
          setShowShareModal(lesson);
          // Paused until decision on product event
          // trackAttioEvent('lesson_shared', {
          //   organizationId: userAccessBadge?.organization?.id,
          //   userId: userAccessBadge?.user?.id,
          //   userRole: userAccessBadge?.user?.profileInfo?.role,
          //   lessonId: lesson.id,
          //   lessonTitle: lesson.content.title,
          //   email: userAccessBadge?.user?.email,
          //   firstName: userAccessBadge?.user?.firstName,
          //   lastName: userAccessBadge?.user?.lastName,
          // });
        }}
      >
        <DropdownMenuItemButton>
          <ShareSvg /> <T keyName="Share" defaultValue="Share" />
        </DropdownMenuItemButton>
      </Menu.Item>
      <Menu.Item key={'seereports'} style={{ padding: 0 }}>
        <Link href={`/${tenantId}/reports/?templateId=${lesson.id}`}>
          <a>
            <DropdownMenuItemButton>
              <ChartSvg /> <T keyName="See reports" defaultValue="See reports" />{' '}
              {lesson.content.livePresentationIds &&
              lesson.content.livePresentationIds.length > 0
                ? `(${lesson.content.livePresentationIds?.length})`
                : ''}
            </DropdownMenuItemButton>
          </a>
        </Link>
      </Menu.Item>
      <Menu.Item key={'downloadPdf'} style={{ padding: 0 }}>
        <DropdownMenuItemButton
          onClick={() => handleDownloadPDFClicked(lesson)}
          disabled={isDownloadingPDF}
        >
          {isDownloadingPDF ? <LoadingOutlined /> : <DownloadSvg />}{' '}
          <T keyName="Download PDF" defaultValue="Download PDF" />
        </DropdownMenuItemButton>
      </Menu.Item>
      <div
        key="divider-folder"
        style={{ width: '100%', height: 1, backgroundColor: '#C4C4C4' }}
      />
      <Menu.Item key={'duplicate'} style={{ padding: 0 }}>
        <DropdownMenuItemButton
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            metrics.logEvent('Lessons.Template.Duplicate');

            setDuplicatingLessonData({
              id: lesson.id,
              parentId: lesson.parentId,
              isPrivate: isWorkspace ? false : true,
              slides: lesson.content.slides,
              title: lesson.content.title,
            });
          }}
        >
          <CopySvg /> <T keyName="Duplicate" defaultValue="Duplicate" />
        </DropdownMenuItemButton>
      </Menu.Item>
      <Menu.Item
        key={'move'}
        style={{ padding: 0 }}
        disabled={lesson.creatorId !== userAccessBadge?.user.id}
        onClick={() =>
          setMovingLessonData({
            id: lesson.id,
            type: 'lesson',
            title: lesson.content.title,
            parentId: lesson.parentId,
            isPrivate: lesson.isPrivate,
            slides: lesson.content.slides,
          })
        }
      >
        <DropdownMenuItemButton disabled={lesson.creatorId !== userAccessBadge?.user.id}>
          <FolderSvg /> <T keyName="Move" defaultValue="Move" />
        </DropdownMenuItemButton>
      </Menu.Item>
      <div
        key="divider-trash"
        style={{ width: '100%', height: 1, backgroundColor: '#C4C4C4' }}
      />
      <Menu.Item
        key={'delete'}
        style={{ padding: 0, color: theme.colors.red }}
        disabled={lesson.creatorId !== userAccessBadge?.user.id}
        onClick={() =>
          setIsDeletingData(convertPresentationDTOToTemplateDataType(lesson))
        }
      >
        <DropdownMenuItemButton disabled={lesson.creatorId !== userAccessBadge?.user.id}>
          <DeleteSvg /> <T keyName="Move to trash" defaultValue="Move to trash" />
        </DropdownMenuItemButton>
      </Menu.Item>
    </Menu>
  );

  const isTouch = isTouchDevice();

  return (
    <SectionGridContainer>
      <SectionGrid $maxOneRow={maxOneRow}>
        {lessons?.map(lesson => (
          <div
            key={lesson.id}
            style={{ height: '100%', width: '100%', position: 'relative' }}
            onMouseEnter={() => setHoveredLessonId(lesson.id)}
            onMouseLeave={() => {
              setHoveredLessonId(undefined);
              setOpenDropdownId(undefined);
            }}
          >
            <div
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setOpenDropdownId(openDropdownId === lesson.id ? undefined : lesson.id);
              }}
            >
              <PresentationCard
                isHovering={lesson.id === hoveredLessonId}
                slides={lesson.content.slides || []}
                subTitle={`Edited:
                    ${dayjs(lesson.modifiedAt).format('MMMM DD, YYYY - hh:mma')}`}
                isGenerator={!!lesson.aiContent?.prompts?.length}
                title={lesson.content.title}
                lessonAttachments={lesson.content.attachments || []}
                isPremiumTestPrep={shouldShowPremiumIndicatorWorkspace(
                  lesson.content.slides || [],
                )}
                hasReachedPremiumTestPrepLimit={premiumTestPrepHasReachedLimitWorkspace}
              />

              <div
                onClick={e => {
                  e.stopPropagation();
                }}
              >
                {(lesson.id === hoveredLessonId ||
                  (lesson.id === creatingLessonId && isCreatingGame) ||
                  isTouch) && (
                  <HoveringPlayButton
                    styles={{
                      position: 'absolute',
                      top: '5px',
                      right: '40px',
                    }}
                    notext
                    icon={<PlaySvg />}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      handlePresent(lesson);
                    }}
                    loading={lesson.id === creatingLessonId && isCreatingGame}
                  />
                )}
                {(lesson.id === hoveredLessonId || isTouch) && (
                  <Dropdown
                    overlay={renderMenu(lesson)}
                    trigger={['click']}
                    placement={'bottomRight'}
                    open={openDropdownId === lesson.id}
                    onVisibleChange={(visible: boolean) => {
                      setOpenDropdownId(visible ? lesson.id : undefined);
                    }}
                  >
                    <MoreButton
                      notext
                      icon={<MoreSvg />}
                      onClick={e => {
                        e.stopPropagation();
                      }}
                    ></MoreButton>
                  </Dropdown>
                )}
              </div>
            </div>
          </div>
        ))}
      </SectionGrid>
      {/* ------------ SHARE LESSON ----------------- */}
      {showShareModal && (
        <BaseModal
          title={t('shareTemplate', {
            defaultValue: 'Share "{{title}}"',
            title: getTitle({
              title: showShareModal.content.title,
              slides: showShareModal.content.slides,
              maxLength: 75,
            }),
          })}
          open={!!showShareModal}
          onCancel={() => {
            setShowShareModal(undefined);
          }}
        >
          <ShareLinkComponent
            templateId={showShareModal.id}
            isGeneratedByAI={!!showShareModal.content?.metadata?.['aiTopic']}
          />
        </BaseModal>
      )}
      {/* ------------ MOVE LESSON ----------------- */}
      {!!movingLessonData && (
        <MoveToFolder
          onCancel={() => setMovingLessonData(undefined)}
          data={movingLessonData}
          onDone={() => setMovingLessonData(undefined)}
        />
      )}
      {/* ------------ DUPLICATE LESSON ----------------- */}
      {!!duplicatingLessonData && (
        <DuplicateLessonToFolder
          onCancel={() => setDuplicatingLessonData(undefined)}
          data={duplicatingLessonData}
          onDone={() => setDuplicatingLessonData(undefined)}
        />
      )}
      {/* ------------ RENAME LESSON ----------------- */}
      {renamingLesson && (
        <BaseModal
          title={t('Rename Lesson', {
            defaultValue: 'Rename "{{title}}"',
            title: renamingLesson.title || t('Untitled'),
          })}
          open
          onCancel={() => setRenamingLesson(undefined)}
        >
          <Input
            placeholder={t('New name')}
            autoFocus
            maxLength={100}
            value={renamingLesson.title}
            disabled={isRenaming}
            onChange={(
              event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) =>
              setRenamingLesson({
                ...renamingLesson,
                title: event.target.value,
              })
            }
            onPressEnter={handleRenameLesson}
          />
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '15px',
            }}
          >
            <Button
              type="tertiary"
              styles={{ border: 'none', color: theme.colors.black, boxShadow: 'none' }}
              onClick={() => setRenamingLesson(undefined)}
            >
              {t('Cancel')}
            </Button>
            <Button disabled={isRenaming} onClick={handleRenameLesson}>
              {isRenaming ? (
                <>
                  <LoadingOutlined /> {t('Renaming')}
                </>
              ) : (
                t('Rename')
              )}
            </Button>
          </div>
        </BaseModal>
      )}
      {/* ------------ MOVE LESSON TO TRASH ----------------- */}
      {isDeletingData && (
        <MoveToTrashModal
          handleDelete={() => {
            deleteLesson({ id: isDeletingData.id });
            setIsDeletingData(undefined);
          }}
          deleteData={isDeletingData}
          isDeletingData={isDeletingLesson}
          folders={[]}
          setDeleteData={t => setIsDeletingData(t as Template | undefined)}
        />
      )}
      {/* ------------ RESUME LESSON MODAL ----------------- */}
      {isOnGoingGameModalVisible &&
        (() => {
          const currentLesson = lessons?.find(l => l.id === isOnGoingGameModalVisible);
          if (!currentLesson) return null;
          return (
            <ResumeLessonModal
              isOnGoingGameModalVisible={!!isOnGoingGameModalVisible}
              onStartNewLesson={() => handleStartNewLesson(currentLesson)}
              onResumeLesson={() => handleResumeLesson(currentLesson)}
              currentPage="lessonPage"
              onEndLesson={handleEndGame}
              onCancel={() => setIsOnGoingGameModalVisible(undefined)}
            />
          );
        })()}
    </SectionGridContainer>
  );
};

const MoreButton = styled(Button)`
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 2;
  padding: 2px 5px;

  background: #00000099;
  border-radius: 5px;
  color: ${theme.colors.white};
  font-size: 12px;
  height: 30px;
  display: flex;
  align-items: center;
  box-shadow: 0px 4px 4px #00000025;

  svg {
    height: 20px;
    width: 20px;
  }
`;

const HoveringPlayButton = styled(MoreButton)`
  position: absolute;
  right: 40px;
  svg {
    height: 15px;
    width: 15px;
  }
`;

const SectionGridContainer = styled.div`
  padding: 10px 20px 40px 20px;
`;

const SectionGrid = styled.div<{ $maxOneRow?: boolean }>`
  padding: 0px 0px 0px 0px;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;

  @media (max-width: 1600px) {
    grid-template-columns: repeat(5, 1fr);
  }
  @media (max-width: 1300px) {
    grid-template-columns: repeat(4, 1fr);
  }
  @media (max-width: 1000px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 800px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 500px) {
    grid-template-columns: repeat(1, 1fr);
  }

  ${({ $maxOneRow }) =>
    $maxOneRow &&
    css`
      height: 250px;
      overflow: hidden;
      @media (max-width: 1600px) {
        height: 200px;
      }
      @media (max-width: 1300px) {
        height: 200px;
      }
      @media (max-width: 1150px) {
        height: 180px;
      }
      @media (max-width: 1000px) {
        height: 195px;
      }
      @media (max-width: 900px) {
        height: 230px;
      }
      @media (max-width: 800px) {
        height: 180px;
      }
      @media (max-width: 700px) {
        height: 220px;
      }
      @media (max-width: 550px) {
        height: 180px;
      }
      @media (max-width: 500px) {
        height: 300px;
      }
      @media (max-width: 450px) {
        height: 260px;
      }
      @media (max-width: 400px) {
        height: 240px;
      }
      @media (max-width: 350px) {
        height: 220px;
      }
    `}
`;

const DropdownMenuItemButton = styled.button`
  background: none;
  border: none;
  padding: 5px 12px;
  cursor: pointer;
  width: 100%;
  text-align: left;
  font-weight: bold;
  &:disabled {
    cursor: not-allowed;
    color: ${theme.colors.grey};
  }
  display: flex;
  align-items: center;
  svg {
    margin-right: 10px;
    margin-left: 5px;
    height: 16px;
    width: 16px;
  }
`;
